import { ChangeEvent, useState } from 'react';
import styled from 'styled-components';
import { DimmedBackground } from '../../dimmed/DimmedBackground';

interface PopupTextInputModalProps {
  isShow: boolean;
  title: string;
  placeholder?: string;
  confirmText: string;
  cancelText?: string;
  confirmHandler: (value: string) => void;
  cancelHandler?: () => void;
}

export const PopupTextInputModal = ({
  isShow,
  title,
  placeholder,
  confirmText,
  cancelText,
  confirmHandler,
  cancelHandler,
}: PopupTextInputModalProps) => {
  const [inputText, setInputText] = useState<string>('');

  if (isShow) {
    return (
      <DimmedBackground isShow={isShow} onClick={() => {}}>
        <Container>
          <Title>{title}</Title>

          <Input
            type="text"
            value={inputText}
            placeholder={placeholder ?? ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setInputText(e.target.value)}
          />

          <ButtonContainer>
            <BlueButton
              onClick={() => {
                confirmHandler(inputText);
                setInputText('');
              }}
            >
              <BlueText>{confirmText}</BlueText>
            </BlueButton>
            {cancelText && (
              <GreyButton
                onClick={() => {
                  if (cancelHandler) {
                    cancelHandler();
                  }
                  setInputText('');
                }}
              >
                <GreyText>{cancelText}</GreyText>
              </GreyButton>
            )}
          </ButtonContainer>
        </Container>
      </DimmedBackground>
    );
  }
  return <></>;
};

const Container = styled.div`
  width: 300px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  padding: 24px;

  gap: 24px;

  position: absolute;
  left: 50%;
  top: 50%;

  transform: translate(-50%, -50%);

  border-radius: var(--radius-large, 16px);
  background: var(--Light-Scheme-Monochrome-white, #fff);
`;

const Title = styled.pre`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Body/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
`;

const ButtonContainer = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  gap: 8px;
`;

const BlueButton = styled.button`
  display: flex;
  max-height: 40px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Color-primary-blue, #445eff);
`;

const BlueText = styled.span`
  color: var(--Light-Scheme-Monochrome-white, #fff);

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;

const GreyButton = styled.button`
  display: flex;
  max-height: 40px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const GreyText = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;

const Input = styled.input`
  height: 40px;
  min-height: 40px;

  display: flex;
  align-items: center;

  padding: 10px 16px;

  gap: 8px;
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  border: 1px solid var(--Light-Scheme-Color-primary-blue, #445eff);
  background: var(--Light-Scheme-Monochrome-white, #fff);
`;
